import clsx from 'clsx';
import * as React from 'react';
import { useInteractOutside, useObjectRef } from 'react-aria';
import { type SubmitHandler, useForm } from 'react-hook-form';

import { FRAGMENT_ANNOTATION_BODY, selectInline } from '@gitbook/doc-core';
import {
    type Change,
    type Fragment,
    type Inline,
    isTextRangeSelection,
    TextRange,
} from '@gitbook/slate';
import {
    type EditorContainer,
    FragmentRenderer,
    type InlineProps,
    ToggleFragmentEditing,
} from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Card,
    type CardProps,
    Form,
    Popover,
    PopoverRoot,
    PopoverTrigger,
    renderIcon,
    Submit,
    TextField,
    TextInput,
} from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { assert } from '@gitbook/util-ts';

/**
 * Component to render an inline annotation node.
 */
export function InlineAnnotation(props: InlineProps) {
    const { editor, children, attributes, node } = props;

    const fragment = node.getFragmentByKey(FRAGMENT_ANNOTATION_BODY);
    assert(fragment, 'Annotation fragment should exist');

    if (editor.readOnly) {
        return (
            <ReadOnlyAnnotation attributes={attributes} fragment={fragment}>
                {children}
            </ReadOnlyAnnotation>
        );
    }

    return (
        <EditableAnnotation attributes={attributes} fragment={fragment} node={node} editor={editor}>
            {children}
        </EditableAnnotation>
    );
}

function ReadOnlyAnnotation(
    props: Pick<InlineProps, 'children' | 'attributes'> & { fragment: Fragment }
) {
    const { children, attributes, fragment } = props;
    return (
        <PopoverRoot triggerAction="hover">
            <PopoverTrigger>
                <AnnotationMark {...attributes} isEditing={false}>
                    {children}
                </AnnotationMark>
            </PopoverTrigger>
            <Popover>
                <AnnotationCard>
                    <ReadOnlyAnnotationFragment fragment={fragment} />
                </AnnotationCard>
            </Popover>
        </PopoverRoot>
    );
}

function EditableAnnotation(
    props: Pick<InlineProps, 'node' | 'editor' | 'children' | 'attributes'> & {
        fragment: Fragment;
    }
) {
    const { node, editor, fragment, children, attributes } = props;

    const isFragmentFocused = editor.value.selection.hasFocusIn(fragment);
    const hasSelectionInNode = editor.value.selection.hasFocusIn(node);
    const hasSelectionInNodeOnly = hasSelectionInNode && !isFragmentFocused;
    const [editedFragment, setEditedFragment] = React.useState<Fragment | null>(
        isFragmentFocused ? fragment : null
    );
    const actionRef = React.useRef<'apply' | 'cancel' | null>(null);
    const isEditing = Boolean(editedFragment);
    const [isManuallyOpen, setIsOpen] = React.useState(isFragmentFocused);
    const isOpen = isManuallyOpen || hasSelectionInNodeOnly;
    const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    const edit = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setEditedFragment(fragment);
        setIsOpen(true);
        editor.change((change) => focusFragment(change, fragment));
    };

    const apply = () => {
        actionRef.current = 'apply';
        setIsOpen(false);
        editor.change((change) => {
            selectInline(change, node);
            change.collapseToStartOfNextText();
        });
    };

    const cancel = (args: { focusEditor: boolean }) => {
        actionRef.current = 'cancel';
        setIsOpen(false);
        if (args.focusEditor) {
            editor.change((change) => {
                selectInline(change, node);
                change.collapseToStartOfNextText();
            });
        }
    };

    const remove = () => {
        editor.change((change) => {
            change.unwrapInlineByKey(node.key);
        });
    };

    const popoverRef = useObjectRef<HTMLDivElement>(null);
    useInteractOutside({
        ref: popoverRef,
        isDisabled: !isEditing || !isOpen,
        onInteractOutside(event) {
            if (hasSelectionInNode) {
                return;
            }
            if (event.target instanceof HTMLElement) {
                if (
                    event.target.closest(
                        '[data-palette-overlay="true"],[data-spine-overlay="true"],[data-popover-overlay="true"]'
                    )
                ) {
                    return;
                }
            }
            cancel({ focusEditor: false });
        },
    });

    const popoverRefHandler = useEventCallback((element: HTMLDivElement | null) => {
        popoverRef.current = element;
        // When the element is unmouted, it means all animations are done
        // we want to change the state once it's invisible to avoid glitches.
        if (!element) {
            // If the user wants to cancel, then we revert changes.
            if (actionRef.current === 'cancel' && editedFragment) {
                editor.change((change) => {
                    change.replaceNodeByKey(fragment.key, editedFragment);
                });
            }

            // In this case we cancel the edit mode.
            setEditedFragment(null);
        }
    });

    return (
        <PopoverRoot
            triggerAction="hover"
            triggerDelay={600}
            isOpen={isOpen}
            onOpenChange={(isOpen) => {
                // If we are in editing mode, we control it ourself.
                if (!isEditing) {
                    setIsOpen(isOpen);
                }
            }}
        >
            <PopoverTrigger>
                <AnnotationMark {...attributes} isEditing={isEditing}>
                    {children}
                </AnnotationMark>
            </PopoverTrigger>
            <Popover
                ref={popoverRefHandler}
                isDismissable={false}
                onKeyDownCapture={(event) => {
                    if (event.key === 'Escape') {
                        event.stopPropagation();
                        cancel({ focusEditor: true });
                    }
                }}
            >
                {isEditing ? (
                    <AnnotationCard className="flex flex-col gap-3">
                        <AnnotationForm editor={editor} node={node} onSubmit={apply} />
                    </AnnotationCard>
                ) : (
                    <AnnotationCard className="flex gap-2">
                        <div className="flex flex-1">
                            <ReadOnlyAnnotationFragment fragment={fragment} />
                        </div>
                        <div className="flex flex-col gap-0.5">
                            <Button
                                size="small"
                                kind="blank"
                                iconOnly
                                leadingIcon={Icon.Edit}
                                label="Edit"
                                onPress={edit}
                            />
                            <Button
                                size="small"
                                kind="blank"
                                iconOnly
                                leadingIcon={Icon.ClearCircle}
                                label="Remove"
                                onPress={remove}
                            />
                        </div>
                    </AnnotationCard>
                )}
            </Popover>
        </PopoverRoot>
    );
}

function ReadOnlyAnnotationFragment(props: { fragment: Fragment }) {
    const { fragment } = props;

    if (fragment.getText().length > 0) {
        return (
            <ToggleFragmentEditing readOnly>
                <FragmentRenderer fragment={FRAGMENT_ANNOTATION_BODY} />
            </ToggleFragmentEditing>
        );
    }

    return <span className="text-muted italic">Empty annotation</span>;
}

type AnnotationFormValues = {
    text: string;
};

function AnnotationForm(props: { editor: EditorContainer; node: Inline; onSubmit: () => void }) {
    const { editor, node } = props;
    const form = useForm<AnnotationFormValues>({
        defaultValues: { text: node.getText() },
    });
    const onSubmit: SubmitHandler<AnnotationFormValues> = ({ text }) => {
        editor.change((change) => {
            const firstText = node.getTexts().first();
            assert(firstText);
            change.replaceTextByKey(firstText.key, 0, firstText.getText().length, text);
        });
        props.onSubmit();
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <TextField form={form} name="text" required>
                <TextInput prefix={renderIcon(Icon.AlignTextLeft, { className: 'text-muted' })} />
            </TextField>
            <div className="flex rounded border p-2">
                <FragmentRenderer fragment={FRAGMENT_ANNOTATION_BODY} />
            </div>
            <Submit
                className="self-end"
                kind="blank"
                leadingIcon={Icon.CheckCircle}
                label="Apply"
                size="small"
                disabled={false}
            />
        </Form>
    );
}

const AnnotationCard = React.forwardRef(function AnnotationCard(
    props: Pick<CardProps, 'className' | 'children'>,
    ref: React.ForwardedRef<HTMLDivElement>
) {
    return (
        <Card
            ref={ref}
            {...props}
            className={clsx(
                'w-screen max-w-72 whitespace-pre-wrap break-words p-3',
                props.className
            )}
        />
    );
});

/**
 * Focus a fragment.
 */
function focusFragment(change: Change, fragment: Fragment) {
    if (!isTextRangeSelection(change.value.selection)) {
        change
            .selectText(TextRange.fromBlockRange(change.value.selection, change.value.document))
            .collapseToEndOf(fragment)
            .focus();
    } else {
        change.collapseToEndOf(fragment).focus();
    }
}

type AnnotationMarkProps = React.ComponentPropsWithoutRef<'span'> & {
    isEditing: boolean;
};

const AnnotationMark = React.forwardRef(function AnnotationMark(
    props: AnnotationMarkProps,
    ref: React.ForwardedRef<HTMLSpanElement>
) {
    const { isEditing, ...rest } = props;
    const spanRef = useObjectRef(ref);
    return (
        <span
            ref={spanRef}
            className={clsx(
                '-mx-1 rounded px-1 underline decoration-2 decoration-muted decoration-dotted',
                isEditing && '-z-[1] bg-active'
            )}
            {...rest}
        />
    );
});
